import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import SupplierServiceConnectionsSection from "./SupplierServiceConnectionsSection";
export { buildServiceConnectionPayload as buildConnectionPayload, buildServiceProfilePayload as buildProfilePayload, serviceLifecycleLabel as lifecycleLabel, safeProfiles, safeServiceConnections, safeApiError } from "./SupplierServiceConnectionsSection";

const SERVER = process.env.REACT_APP_NODEJS_SERVER_URL;
const FORBIDDEN_RESPONSE_KEYS = /^(apiKey|password|secretJson|secretPayload|encryptedSecretPayload|apiKeyEncrypted|passwordEncrypted|encryptedSecret|promoStandardsId|accessToken|refreshToken|clientSecret|authorization|maskedSecret)$/i;

export const containsSecretField = (value) => {
  const seen = new Set();
  const inspect = current => {
    if (!current || typeof current !== "object") return false;
    if (seen.has(current)) return true;
    seen.add(current);
    if (Object.getPrototypeOf(current) !== Object.prototype && !Array.isArray(current)) return true;
    if (Object.getOwnPropertySymbols(current).length) return true;
    return Object.keys(Object.getOwnPropertyDescriptors(current)).some(key => {
      const descriptor = Object.getOwnPropertyDescriptor(current, key);
      return FORBIDDEN_RESPONSE_KEYS.test(key) || !descriptor || !Object.prototype.hasOwnProperty.call(descriptor, "value") || inspect(descriptor.value);
    });
  };
  return inspect(value);
};

const statusLabel = (connection) => {
  if (!connection?.enabled && connection?.hasCredentials) return "Disabled";
  const status = String(connection?.connectionStatus || "not_configured").toLowerCase();
  if (status === "connected") return "Connected";
  if (status === "not_configured") return "Not configured";
  if (status === "error" && connection?.lastTestStatus === "failed") return "Connection failed";
  return status.replace(/_/g, " ").replace(/^./, char => char.toUpperCase());
};

const statusColor = (connection) => {
  if (!connection?.enabled && connection?.hasCredentials) return "default";
  if (connection?.connectionStatus === "connected") return "success";
  if (connection?.connectionStatus === "error") return "error";
  return "default";
};

export const safeConnection = (response) => {
  const source = response && response.connection;
  if (!source || typeof source !== "object" || Object.getPrototypeOf(source) !== Object.prototype) return null;
  const allowed = ["supplierId", "provider", "providerDisplayName", "sourceType", "enabled", "configured", "connected", "hasCredentials", "connectionStatus", "authType", "baseUrl", "accountId", "capabilities", "preferredForCatalogueImport", "lastTestStatus", "lastTestedAt", "lastTestMessage"];
  const out = {};
  for (const key of allowed) {
    const descriptor = Object.getOwnPropertyDescriptor(source, key);
    if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, "value")) continue;
    if (FORBIDDEN_RESPONSE_KEYS.test(key)) continue;
    const value = descriptor.value;
    if (value == null || ["string", "boolean", "number"].includes(typeof value)) out[key] = value;
    else if (key === "capabilities" && value && Object.getPrototypeOf(value) === Object.prototype) out[key] = { ...value };
  }
  return out;
};

export const safeConnections = (response) => {
  if (!response || !Array.isArray(response.connections)) return [];
  return response.connections.map(item => safeConnection({ connection: item }));
};
const getOwnDataValue = (value, key) => { if (!value || typeof value !== "object") return undefined; const descriptor = Object.getOwnPropertyDescriptor(value, key); return descriptor && Object.prototype.hasOwnProperty.call(descriptor, "value") ? descriptor.value : undefined; };
const legacyErrorDetails = error => { const response = getOwnDataValue(error, "response"); const data = getOwnDataValue(response, "data"); return { code: getOwnDataValue(data, "code"), status: getOwnDataValue(response, "status") }; };
export { getOwnDataValue };
export const safeLegacyApiError = (requestError, fallbackMessage) => {
  const { code } = legacyErrorDetails(requestError);
  const messages = { UNSUPPORTED_SUPPLIER: "This supplier is not supported.", SUPPLIER_NOT_FOUND: "Supplier was not found.", UNAUTHORIZED: "You are not authorized to perform this action." };
  return typeof code === "string" && messages[code] ? messages[code] : fallbackMessage;
};

const SupplierApiIntegrationTab = ({ instanceUniqueId, supplierId, supplierName, sourceSupplierId }) => {
  const [connection, setConnection] = useState(null);
  const [connections, setConnections] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [promoStandardsId, setPromoStandardsId] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [unsupported, setUnsupported] = useState(false);
  const mountedRef = useRef(true);
  const scopeRef = useRef({ instanceUniqueId, supplierId, generation: 0 });
  const loadSeqRef = useRef(0);
  const selectedProviderRef = useRef(null);
  const operationLocksRef = useRef(new Map());
  const beginOperation = key => { if (operationLocksRef.current.has(key)) return null; const token = {}; operationLocksRef.current.set(key, token); return token; };
  const isOperationCurrent = (key, token, scope) => mountedRef.current && scopeRef.current.generation === scope.generation && operationLocksRef.current.get(key) === token;

  const headers = useMemo(() => ({ accessToken: localStorage.getItem("accessToken") }), []);

  const applyConnection = useCallback((next) => {
    selectedProviderRef.current = next?.provider || null;
    setConnection(next);
    setEnabled(Boolean(next?.enabled));
    setAccountId(next?.accountId || "");
    setApiKey("");
    setPromoStandardsId("");
  }, []);

  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);
  const loadConnection = useCallback(async () => {
    if (!instanceUniqueId || !supplierId) return;
    const scope = { ...scopeRef.current }; const sequence = ++loadSeqRef.current;
    setLoading(true);
    setError("");
    setUnsupported(false);
    try {
      const { data } = await axios.get(`${SERVER}/suppliers/${supplierId}/api-integrations`, {
        params: { instanceUniqueId },
        headers,
      });
      const nextConnections = safeConnections(data);
      if (!mountedRef.current || scopeRef.current.generation !== scope.generation || sequence !== loadSeqRef.current) return;
      setConnections(nextConnections);
      const selected = nextConnections.find(item => item.provider === selectedProviderRef.current) || nextConnections[0] || null;
      if (!selected) setUnsupported(true);
      else applyConnection(selected);
    } catch (requestError) {
      if (!mountedRef.current || scopeRef.current.generation !== scope.generation || sequence !== loadSeqRef.current) return;
      const { code, status } = legacyErrorDetails(requestError);
      if (["UNSUPPORTED_SUPPLIER", "SUPPLIER_NOT_FOUND"].includes(code) || status === 404) {
        setUnsupported(true);
      } else {
        setError(safeLegacyApiError(requestError, "Failed to load catalogue API integration."));
      }
    } finally {
      if (mountedRef.current && scopeRef.current.generation === scope.generation && sequence === loadSeqRef.current) setLoading(false);
    }
  }, [applyConnection, headers, instanceUniqueId, supplierId]);

  useEffect(() => {
    if (scopeRef.current.instanceUniqueId !== instanceUniqueId || scopeRef.current.supplierId !== supplierId) {
      scopeRef.current = { instanceUniqueId, supplierId, generation: scopeRef.current.generation + 1 };
      selectedProviderRef.current = null;
      operationLocksRef.current = new Map();
      setSaving(false);
      setTesting(false);
      setApiKey("");
      setPromoStandardsId("");
    }
    loadConnection();
  }, [loadConnection]);

  const isPromoStandards = connection?.authType === "promostandards_credentials";
  const dirty = Boolean(apiKey) || Boolean(promoStandardsId) || enabled !== Boolean(connection?.enabled) || (!isPromoStandards && accountId !== (connection?.accountId || ""));
  const canTest = Boolean(connection?.hasCredentials && connection?.enabled && connection?.capabilities?.testConnection && !dirty);

  const save = async () => {
    const scope = { ...scopeRef.current };
    const token = beginOperation("legacy-write"); if (!token) return;
    setSaving(true);
    setError("");
    try {
      const payload = { instanceUniqueId, enabled };
      if (isPromoStandards) {
        if (promoStandardsId.trim()) payload.promoStandardsId = promoStandardsId.trim();
        if (apiKey.trim()) payload.password = apiKey.trim();
      } else {
        if (accountId.trim()) payload.accountId = accountId.trim();
        else if (connection?.accountId) payload.accountId = null;
        if (apiKey.trim()) payload.apiKey = apiKey.trim();
      }
      const { data } = await axios.put(`${SERVER}/suppliers/${supplierId}/api-integrations/${connection.provider}`, payload, { headers });
      if (!isOperationCurrent("legacy-write", token, scope)) return;
      applyConnection(safeConnection(data));
      await loadConnection();
      if (isOperationCurrent("legacy-write", token, scope)) toast.success("Supplier API integration saved.");
    } catch (requestError) {
      const message = safeLegacyApiError(requestError, "Failed to save supplier API integration.");
      if (isOperationCurrent("legacy-write", token, scope)) { setError(message); toast.error(message); }
    } finally {
      if (isOperationCurrent("legacy-write", token, scope)) setSaving(false); if (operationLocksRef.current.get("legacy-write") === token) operationLocksRef.current.delete("legacy-write");
    }
  };

  const testConnection = async () => {
    if (!canTest) return;
    const scope = { ...scopeRef.current }; setTesting(true);
    const token = beginOperation("legacy-test"); if (!token) return;
    setError("");
    try {
      const { data } = await axios.post(
        `${SERVER}/suppliers/${supplierId}/api-integrations/${connection.provider}/test`,
        { instanceUniqueId },
        { headers }
      );
      if (!isOperationCurrent("legacy-test", token, scope)) return;
      applyConnection(safeConnection(data));
      toast.success("Supplier API connection test succeeded.");
    } catch (requestError) {
      const message = safeLegacyApiError(requestError, "Connection test failed.");
      if (!isOperationCurrent("legacy-test", token, scope)) return;
      toast.error(message);
      await loadConnection();
      if (isOperationCurrent("legacy-test", token, scope)) setError(message);
    } finally {
      if (isOperationCurrent("legacy-test", token, scope)) setTesting(false); if (operationLocksRef.current.get("legacy-test") === token) operationLocksRef.current.delete("legacy-test");
    }
  };

  const setPreferred = async () => {
    const scope = { ...scopeRef.current };
    const token = beginOperation("legacy-write"); if (!token) return;
    setSaving(true);
    setError("");
    try {
      await axios.post(`${SERVER}/suppliers/${supplierId}/api-integrations/${connection.provider}/preferred`, { instanceUniqueId }, { headers });
      await loadConnection();
      if (isOperationCurrent("legacy-write", token, scope)) toast.success("Preferred catalogue provider updated.");
    } catch (requestError) {
      const message = safeLegacyApiError(requestError, "Failed to set preferred provider.");
      if (isOperationCurrent("legacy-write", token, scope)) { setError(message); toast.error(message); }
    } finally { if (isOperationCurrent("legacy-write", token, scope)) setSaving(false); if (operationLocksRef.current.get("legacy-write") === token) operationLocksRef.current.delete("legacy-write"); }
  };

  const identityNotice = sourceSupplierId != null && String(sourceSupplierId) !== String(supplierId) ? <Alert severity="info">
    Viewing {supplierName} under supplier record {sourceSupplierId}; Supplier API connections use canonical CoreSupplier ID {supplierId}.
  </Alert> : null;

  if (loading) {
    return <Box sx={{ p: 2, display: "grid", gap: 2 }}><SupplierServiceConnectionsSection instanceUniqueId={instanceUniqueId} supplierCoreId={supplierId} />{identityNotice}<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}><CircularProgress size={20} /><Typography>Loading API integration...</Typography></Box></Box>;
  }

  if (unsupported) {
    return <Box sx={{ p: 2, display: "grid", gap: 2 }}><SupplierServiceConnectionsSection instanceUniqueId={instanceUniqueId} supplierCoreId={supplierId} />{identityNotice}<Box><Typography variant="h6">Registered Catalogue Preview</Typography><Alert severity="info">No environment-registered preview is configured for this supplier.</Alert></Box></Box>;
  }

  return (
    <Box sx={{ p: 2, display: "grid", gap: 2 }}>
      <SupplierServiceConnectionsSection instanceUniqueId={instanceUniqueId} supplierCoreId={supplierId} />
      {identityNotice}
      <Typography variant="h6">Registered Catalogue Preview</Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <Box>
          <Typography variant="h6">API Integration</Typography>
          <Typography variant="body2" color="text.secondary">Catalogue API for {supplierName}</Typography>
        </Box>
        <Chip label={statusLabel(connection)} color={statusColor(connection)} variant={connection?.connectionStatus === "connected" ? "filled" : "outlined"} />
      </Box>

      {error && <Alert severity="error">{error}</Alert>}
      {connections.length > 1 && (
        <Box sx={{ display: "grid", gap: 1 }}>
          {connections.map(item => (
            <Button key={item.provider} variant={item.provider === connection?.provider ? "contained" : "outlined"} onClick={() => applyConnection(item)} sx={{ justifyContent: "space-between" }}>
              <span>{item.providerDisplayName || item.provider}</span>
              <span>{item.preferredForCatalogueImport ? "Preferred" : statusLabel(item)}</span>
            </Button>
          ))}
        </Box>
      )}
      {connection?.capabilities?.testConnection === false && (
        <Alert severity="info">API configuration can be saved. Connection testing will be available when this supplier's catalogue adapter is enabled.</Alert>
      )}

      <TextField label="Provider" value={connection?.providerDisplayName || connection?.provider || ""} InputProps={{ readOnly: true }} fullWidth />
      {!isPromoStandards && connection?.baseUrl && <TextField label="API Base URL" value={connection.baseUrl} InputProps={{ readOnly: true }} helperText="Managed by trusted server configuration." fullWidth />}
      {isPromoStandards && <TextField label="Account / Customer ID" type="password" value={promoStandardsId} onChange={event => setPromoStandardsId(event.target.value)} placeholder={connection?.hasCredentials ? "Enter a new credential to replace existing" : "Enter account / customer ID"} helperText={connection?.hasCredentials ? "Credential saved. Leave blank to keep the existing credential." : "No credential saved."} autoComplete="new-password" fullWidth />}
      <TextField
        label={isPromoStandards ? "Password" : "API Key"}
        type="password"
        value={apiKey}
        onChange={event => setApiKey(event.target.value)}
        placeholder={connection?.hasCredentials ? "Enter a new credential to replace existing" : (isPromoStandards ? "Enter password" : "Enter API key")}
        helperText={connection?.hasCredentials ? "Credential saved. Leave blank to keep the existing credential." : "No credential saved."}
        autoComplete="new-password"
        fullWidth
      />
      {connection?.accountId != null && (
        <TextField label="Account ID" value={accountId} onChange={event => setAccountId(event.target.value)} fullWidth />
      )}
      <FormControlLabel
        control={<Switch checked={enabled} onChange={event => setEnabled(event.target.checked)} inputProps={{ "aria-label": "Enabled" }} />}
        label="Enabled"
      />

      <Box>
        <Typography variant="body2"><strong>Last tested:</strong> {connection?.lastTestedAt ? new Date(connection.lastTestedAt).toLocaleString() : "Not tested yet"}</Typography>
        {connection?.lastTestMessage && <Typography variant="body2" color="text.secondary">{connection.lastTestMessage}</Typography>}
      </Box>
      {dirty && <Typography variant="caption" color="text.secondary">Save changes before testing the connection.</Typography>}

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        {connection?.configured && connection?.enabled && connection?.connected && connection?.capabilities?.catalogueImport && !connection?.preferredForCatalogueImport && (
          <Button variant="text" onClick={setPreferred} disabled={dirty || saving || testing}>Set as preferred</Button>
        )}
        <Button variant="outlined" onClick={testConnection} disabled={!canTest || saving || testing}>
          {testing ? "Testing..." : "Test Connection"}
        </Button>
        <Button variant="contained" onClick={save} disabled={!dirty || saving || testing}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </Box>
    </Box>
  );
};

export default SupplierApiIntegrationTab;
